<template>
  <div class="chatbot-wrapper">
    <div class="chatbot">
      <div v-if="messages.length" ref="content" class="scrollable-content">
        <Node v-for="(conv, index) in messages" :key="index">
          <ErrorMessage v-if="isErrorMessage(conv.messageType)" :message="conv.message" />
          <ContextMessage v-else-if="isContextMessage(conv.messageType)" :message="conv" />
          <Message v-else :message="conv" />
        </Node>
      </div>
      <div v-else class="no-messages-title">
        <div class="icon">
          <img :src="productIcon" alt="product" />
        </div>
        <div>{{ t('conversation.whatCanIHelp') }}</div>
      </div>
    </div>
    <div class="operations-container">
      <ButtonWithIcon
        v-if="isCollectionActive"
        v-has-access-by-role:hide="{ role: ROLE_TYPE.IS_BETA_USER }"
        :icon="isResearchModeOn ? 'checkmark-small' : ''"
        :selected="isResearchModeOn"
        text-key="assist.researchMode"
        @click="handleIsResearchModeOn"
      />
    </div>
    <MessageInput @onSubmit="onSubmitValue" />
  </div>
</template>

<script>
import { computed, nextTick, onUpdated, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';

import productIcon from '~/assets/svg/product-icon.svg';

import { DEFAULT_COLLECTION, messageType, queryType } from '~/constants/conversationModels';
import { ROLE_TYPE } from '~/constants/user';

import Message from '~/components/conversation/chatMessage/Message';
import Node from '~/components/conversation/chatMessage/Node';
import MessageInput from '~/components/conversation/MessageInput';
import useConversations from '~/composables/useConversations';
import ContextMessage from '~/components/conversation/chatMessage/ContextMessage';
import ErrorMessage from '~/components/conversation/chatMessage/ErrorMessage';
import ButtonWithIcon from '~/components/conversation/ButtonWithIcon.vue';

export default {
  name: 'Conversation',
  computed: {
    ROLE_TYPE() {
      return ROLE_TYPE;
    },
  },
  components: {
    ContextMessage,
    MessageInput,
    Message,
    Node,
    ErrorMessage,
    ButtonWithIcon,
  },
  setup() {
    const { t } = useI18n();
    const {
      createNewConversation,
      currentConversation,
      publishToConversation,
      setIsNewConversation,
      isResearchModeOn,
      setIsResearchModeOn,
    } = useConversations();
    const { conversationScope } = useConversations();

    const route = useRoute();

    const messages = computed(() => currentConversation.value.messages);

    const conversationId = computed(() => currentConversation.value.conversationId);

    const isCollectionActive = computed(() => conversationScope.value.isScoped);

    const content = ref(null);

    const conversationStack = ref([]);

    const isContextMessage = (type) => type === messageType.context;
    const isErrorMessage = (type) => type === messageType.error;

    const scrollToBottom = () => {
      if (content.value) {
        content.value.scrollTop = content.value.scrollHeight;
      }
    };

    const shouldShowScopeMessage = (message, index) => {
      // leave this to help us when be is ready
      // Avoid showing scope message if it's the first message and scope collection is vault
      if (index === 0 && message.collectionId === DEFAULT_COLLECTION.collectionId) {
        return false;
      }
      // If it's the first message (but not 'vault'), or collections are different from the previous message
      return index === 0 || messages.value[index - 1].collectionId !== message.collectionId;
    };

    const onSubmitValue = async (value) => {
      // this means we are on a new conversation use case
      if (!route.params.conversationId) {
        // need to take into consideration this corner case
        // new generated convId is pushed to route path but this conv has not been saved in db
        setIsNewConversation(true);
        await createNewConversation();
      }

      await publishToConversation(conversationId.value, queryType.search, value);
    };

    const handleIsResearchModeOn = () => {
      setIsResearchModeOn(!isResearchModeOn.value);
    };

    onUpdated(() => {
      const isTyping = messages.value.some((message) => message.isTyping);

      if (isTyping) {
        // Use nextTick to wait for the DOM to update
        nextTick(() => {
          scrollToBottom();
        });
      }
    });

    watch(
      () => messages.value[messages.value.length - 1] && messages.value[messages.value.length - 1].data?.length,
      (newLength, oldLength) => {
        if (newLength !== oldLength) {
          // Ensures scroll to bottom when references are concatenated to the last message created by combining chunks
          nextTick(() => {
            scrollToBottom();
          });
        }
      },
      { deep: true },
    );
    watch(
      () => messages.value.length,
      (newLength, oldLength) => {
        if (newLength !== oldLength) {
          // Ensures scroll to bottom when component is mounted for the first time and new messages are injected into conversation(system messages)
          nextTick(() => {
            scrollToBottom();
          });
        }
      },
      { deep: true },
    );
    watch(
      () => conversationScope.value.isScoped,
      (isScoped) => {
        if (!isScoped && isResearchModeOn.value) {
          setIsResearchModeOn(false);
        }
      },
    );

    return {
      t,
      content,
      conversationStack,
      isContextMessage,
      isErrorMessage,
      messages,
      onSubmitValue,
      shouldShowScopeMessage,
      isResearchModeOn,
      handleIsResearchModeOn,
      isCollectionActive,
      productIcon,
    };
  },
};
</script>

<style lang="scss" scoped>
@import '../../assets/styles/variables.scss';

.chatbot-wrapper {
  --primary-color: #{$bb-primary-1};
  --primary-dimmed-color: #{$bb-primary-1-dimmed};

  @include bb-base-font-family;
  @include bb-regular-text-styles('md');

  @include media-breakpoint-up('sm') {
    // padding: 0 156px $bb-spacing-4 0;
    // padding: 0 $bb-spacing-8 0 $bb-spacing-8;
  }

  height: 100%;

  color: $bb-neutral-1;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: antialiased;
  font-smoothing: antialiased;
  -webkit-text-size-adjust: 100%; /* Prevent adjustments of font size after orientation changes in iOS */

  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  width: 100%;
  position: relative;
  flex: 1;
  max-width: 1024px;
  margin: auto;
  padding: $bb-spacing-4;

  .operations-container {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: $bb-spacing-5;
    min-height: $bb-spacing-6;
  }

  .chatbot {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: auto;
    justify-content: flex-end;

    .no-messages-title {
      font-size: 38px !important;
      margin: auto;
      display: flex;
      align-items: center;
      gap: $bb-spacing-6;

      .icon {
        background: #fdb91a;
        border-radius: 100px;
        padding: $bb-spacing-2;
      }
    }
  }

  .scrollable-content {
    overflow: auto;
    max-height: 100%;
    padding-right: $bb-spacing-4;
  }
}
</style>
